import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { API_DOMAIN } from "../constants";
import { useAuth } from "./AuthContext";
import { triggerUnauthorized } from "../axiosSetup";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { authState } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  // Presence of this SRM user's dedicated WATI keep-alive connection —
  // separate from WatiSocketContext's shared chat-update listener.
  const [watiPresence, setWatiPresence] = useState(null);

  useEffect(() => {
    console.log('🔍 SocketProvider - Auth state:', {
      hasId: !!authState?.id,
      isAuthenticated: authState?.isAuthenticated
    });

    if (!authState?.id || !authState.isAuthenticated) {
      console.log('🔍 SocketProvider - No auth, cleaning up socket');
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
        setWatiPresence(null);
      }
      return;
    }

    console.log('🚀 SocketProvider - Creating socket connection for user:', authState.id);

    const newSocket = io(API_DOMAIN, {
      // The backend derives the user id from this JWT — it no longer trusts
      // a client-supplied userId. A missing/invalid/expired token rejects
      // the connection with connect_error "not logged in".
      auth: {
        accessToken: authState.token,
      },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 2000,
    });

    newSocket.on("connect", () => {
      console.log(`✅ Socket Connected: ${newSocket.id}`);
      setIsConnected(true);
      // Backend also emits this automatically post-connect; requesting it
      // too covers the case where that initial emit is missed.
      newSocket.emit("wati:presence:get", (response) => {
        if (response?.data) setWatiPresence(response.data);
      });
    });

    newSocket.on("disconnect", (reason) => {
      console.log("❌ Socket Disconnected:", reason);
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("🔴 Socket Connection Error:", error);
      setIsConnected(false);
      if (error.message === "not logged in") {
        triggerUnauthorized();
      }
    });

    newSocket.on("wati:presence", (payload) => {
      if (payload?.data) setWatiPresence(payload.data);
    });

    setSocket(newSocket);

    return () => {
      console.log('🔍 SocketProvider - Cleanup');
      newSocket.disconnect();
    };
  }, [authState?.id, authState?.isAuthenticated, authState?.token]);

  const emitEvent = useCallback((event, data) => {
    if (socket && isConnected) {
      socket.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit event:', event);
    }
  }, [socket, isConnected]);

  const refreshWatiPresence = useCallback(() => {
    if (!socket || !isConnected) return;
    socket.emit("wati:presence:get", (response) => {
      if (response?.data) setWatiPresence(response.data);
    });
  }, [socket, isConnected]);

  const value = {
    socket,
    isConnected,
    emitEvent,
    watiPresence,
    refreshWatiPresence,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
